import { useNavigate } from 'react-router';
import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from '@/lib/api-client';
import { AnimatePresence, motion } from 'framer-motion';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import {
    X,
    Upload,
    ClipboardPaste,
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    FileJson,
    AlertCircle,
    Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { logger } from '@/lib/telemetry';
import type { AslDefinition } from '@/types/workflow-graph';

// =============================================================================
// Types
// =============================================================================

type ImportMethod = 'upload' | 'paste';

interface ImportAslDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    workflowId: string;
    workflowKey: string;
    /** App ID when workflow is scoped to an app */
    appSlug?: string;
    /** Callback when import is successful - receives the new definition */
    onImportSuccess?: (definition: AslDefinition) => void;
    /** Base URL for the workflow update endpoint (overrides default) */
    updateUrl?: string;
}

// =============================================================================
// Validation Helper
// =============================================================================

function validateAslDefinition(json: unknown): { valid: boolean; error?: string; definition?: AslDefinition } {
    if (typeof json !== 'object' || json === null) {
        return { valid: false, error: 'Invalid JSON: Expected an object' };
    }

    const def = json as Record<string, unknown>;

    // Check required ASL fields
    if (typeof def.StartAt !== 'string' || !def.StartAt) {
        return { valid: false, error: 'Missing or invalid "StartAt" field' };
    }

    if (typeof def.States !== 'object' || def.States === null || Array.isArray(def.States)) {
        return { valid: false, error: 'Missing or invalid "States" field' };
    }

    const states = def.States as Record<string, unknown>;

    // Check that StartAt references a valid state
    if (!(def.StartAt in states)) {
        return { valid: false, error: `StartAt references unknown state: "${def.StartAt}"` };
    }

    // Check that each state has a Type
    for (const [stateName, state] of Object.entries(states)) {
        if (typeof state !== 'object' || state === null) {
            return { valid: false, error: `State "${stateName}" is not a valid object` };
        }
        const stateObj = state as Record<string, unknown>;
        if (typeof stateObj.Type !== 'string') {
            return { valid: false, error: `State "${stateName}" is missing the "Type" field` };
        }
    }

    return { valid: true, definition: json as AslDefinition };
}

// =============================================================================
// Main Component
// =============================================================================

export default function ImportAslDrawer({
    isOpen,
    onClose,
    workflowId,
    workflowKey,
    appSlug,
    onImportSuccess,
    updateUrl,
}: ImportAslDrawerProps) {
    // Wizard state
    const [step, setStep] = useState(1);
    const [importMethod, setImportMethod] = useState<ImportMethod>('upload');
    const [loading, setLoading] = useState(false);

    // File upload state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Paste state
    const [pastedContent, setPastedContent] = useState('');

    // Validation state
    const [validationError, setValidationError] = useState<string | null>(null);
    const [parsedDefinition, setParsedDefinition] = useState<AslDefinition | null>(null);

    // Reset state when drawer opens
    const handleOpen = useCallback(() => {
        setStep(1);
        setImportMethod('upload');
        setSelectedFile(null);
        setPastedContent('');
        setValidationError(null);
        setParsedDefinition(null);
        setLoading(false);
    }, []);

    // Reset when closing
    const handleClose = useCallback(() => {
        handleOpen();
        onClose();
    }, [handleOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            handleOpen();
        }
    }, [isOpen, handleOpen]);

    // Handle file selection
    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setValidationError(null);
            setParsedDefinition(null);
        }
    }, []);

    // Process file content
    const processFileContent = useCallback(async (): Promise<{ valid: boolean; definition?: AslDefinition }> => {
        if (!selectedFile) {
            setValidationError('No file selected');
            return { valid: false };
        }

        try {
            const content = await selectedFile.text();
            const json = JSON.parse(content);
            const result = validateAslDefinition(json);
            
            if (!result.valid) {
                setValidationError(result.error || 'Invalid ASL definition');
                return { valid: false };
            }

            setParsedDefinition(result.definition!);
            setValidationError(null);
            return { valid: true, definition: result.definition };
        } catch (err) {
            const message = err instanceof SyntaxError 
                ? 'Invalid JSON syntax: ' + err.message 
                : 'Failed to read file';
            setValidationError(message);
            return { valid: false };
        }
    }, [selectedFile]);

    // Process pasted content
    const processPastedContent = useCallback((): { valid: boolean; definition?: AslDefinition } => {
        if (!pastedContent.trim()) {
            setValidationError('No content provided');
            return { valid: false };
        }

        try {
            const json = JSON.parse(pastedContent);
            const result = validateAslDefinition(json);
            
            if (!result.valid) {
                setValidationError(result.error || 'Invalid ASL definition');
                return { valid: false };
            }

            setParsedDefinition(result.definition!);
            setValidationError(null);
            return { valid: true, definition: result.definition };
        } catch (err) {
            const message = err instanceof SyntaxError 
                ? 'Invalid JSON syntax: ' + err.message 
                : 'Failed to parse content';
            setValidationError(message);
            return { valid: false };
        }
    }, [pastedContent]);

    // Handle next step
    const handleNext = useCallback(async () => {
        if (step === 1) {
            setStep(2);
            setValidationError(null);
            return;
        }

        if (step === 2) {
            // Validate content before moving to confirmation
            let result: { valid: boolean; definition?: AslDefinition };
            
            if (importMethod === 'upload') {
                result = await processFileContent();
            } else {
                result = processPastedContent();
            }

            if (result.valid) {
                setStep(3);
            }
        }
    }, [step, importMethod, processFileContent, processPastedContent]);

    // Handle back
    const handleBack = useCallback(() => {
        if (step > 1) {
            setStep(step - 1);
            setValidationError(null);
        }
    }, [step]);

    // Handle import confirmation
    const handleImport = useCallback(async () => {
        if (!parsedDefinition) {
            toast.error('No valid definition to import');
            return;
        }

        setLoading(true);

        try {
            // Construct URL based on app context - use applications/capabilities API
            if (!appSlug && !updateUrl) {
                toast.error('Import failed', { description: 'App context is required to import workflows.' });
                setLoading(false);
                return;
            }
            const baseUrl = appSlug ? `/applications/${appSlug}/capabilities` : '';
            const url = updateUrl || `${baseUrl}/${encodeURIComponent(workflowKey)}`;
            
            logger.info('Importing ASL definition', {
                workflowId,
                workflowKey,
                stateCount: Object.keys(parsedDefinition.States || {}).length,
            });

            // Backend expects type_config with ASL content (States, StartAt, Comment) in working revision
            await api.put(url, { type_config: parsedDefinition });
            toast.success('Workflow imported successfully', {
                description: `Imported ${Object.keys(parsedDefinition.States || {}).length} states`,
            });
            onImportSuccess?.(parsedDefinition);
            handleClose();
            window.location.reload();
        } catch (err: unknown) {
            const apiErrors = (err as Record<string, unknown> & { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors;
            if (apiErrors) {
                logger.error('Failed to import workflow', { errors: apiErrors });
                const errorMessage = Object.values(apiErrors).flat().join(', ');
                toast.error('Import failed', { description: errorMessage });
            } else {
                logger.error('Import error', { error: err instanceof Error ? err.message : String(err) });
                toast.error('Import failed', { description: 'An unexpected error occurred' });
            }
            setLoading(false);
        }
    }, [parsedDefinition, updateUrl, appSlug, workflowId, workflowKey, onImportSuccess, handleClose]);

    // Check if can proceed to next step
    const canProceed = step === 1 
        ? true 
        : step === 2 
            ? (importMethod === 'upload' ? !!selectedFile : !!pastedContent.trim())
            : !!parsedDefinition;

    // Step labels
    const stepLabels = ['Method', 'Content', 'Confirm'];

    return (
        <SlideDrawer isOpen={isOpen} onClose={handleClose}>
                        {/* Header */}
                        <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                                        <Upload className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            Import ASL Definition
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            Import a workflow definition from file or paste
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleClose();
                                    }}
                                    className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Stepper */}
                            <div className="flex items-center gap-4">
                                {[1, 2, 3].map((s) => (
                                    <div key={s} className="flex items-center gap-2">
                                        <div
                                            className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                                                step === s
                                                    ? 'border-wizard-step-current-border bg-wizard-step-current text-wizard-step-current-text'
                                                    : step > s
                                                        ? 'border-wizard-step-completed-border bg-wizard-step-completed text-wizard-step-completed-text'
                                                        : 'border-wizard-step-pending-border bg-wizard-step-pending text-wizard-step-pending-text'
                                            }`}
                                        >
                                            {step > s ? <CheckCircle2 size={16} /> : s}
                                        </div>
                                        <span
                                            className={`text-sm font-medium transition-colors ${
                                                step === s ? 'text-slate-900' : 'text-slate-500'
                                            }`}
                                        >
                                            {stepLabels[s - 1]}
                                        </span>
                                        {s < 3 && <div className="h-px w-12 bg-slate-200" />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-16 py-8">
                            <AnimatePresence mode="wait">
                                {/* Step 1: Choose Import Method */}
                                {step === 1 && (
                                    <motion.div
                                        key="step1"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="space-y-6"
                                    >
                                        <div>
                                            <h3 className="text-lg font-medium text-slate-900 mb-2">
                                                How would you like to import?
                                            </h3>
                                            <p className="text-sm text-slate-500">
                                                Choose to upload a JSON file or paste the ASL definition directly.
                                            </p>
                                        </div>

                                        <RadioGroup
                                            value={importMethod}
                                            onValueChange={(value) => setImportMethod(value as ImportMethod)}
                                            className="space-y-4"
                                        >
                                            {/* Upload Option */}
                                            <label
                                                className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                                    importMethod === 'upload'
                                                        ? 'border-blue-500 bg-blue-50/50'
                                                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                }`}
                                            >
                                                <RadioGroupItem value="upload" className="mt-1" />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <Upload size={18} className="text-blue-600" />
                                                        <span className="font-medium text-slate-900">
                                                            Upload Definition File
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-slate-500 mt-1">
                                                        Select a JSON file containing your ASL workflow definition.
                                                    </p>
                                                </div>
                                            </label>

                                            {/* Paste Option */}
                                            <label
                                                className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                                    importMethod === 'paste'
                                                        ? 'border-blue-500 bg-blue-50/50'
                                                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                }`}
                                            >
                                                <RadioGroupItem value="paste" className="mt-1" />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <ClipboardPaste size={18} className="text-violet-600" />
                                                        <span className="font-medium text-slate-900">
                                                            Paste ASL Definition
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-slate-500 mt-1">
                                                        Paste the JSON ASL definition directly into a text area.
                                                    </p>
                                                </div>
                                            </label>
                                        </RadioGroup>
                                    </motion.div>
                                )}

                                {/* Step 2: Provide Content */}
                                {step === 2 && (
                                    <motion.div
                                        key="step2"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="h-full flex flex-col"
                                    >
                                        {importMethod === 'upload' ? (
                                            /* File Upload */
                                            <div className="flex-1 flex flex-col">
                                                <Label className="mb-4 text-base font-medium text-slate-900">
                                                    Select ASL Definition File
                                                </Label>
                                                
                                                <div
                                                    className={`flex-1 border-2 border-dashed rounded-lg transition-all cursor-pointer relative group flex flex-col items-center justify-center p-8 text-center min-h-[300px] ${
                                                        selectedFile
                                                            ? 'border-green-300 bg-green-50/50'
                                                            : 'border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-blue-300'
                                                    }`}
                                                    onClick={() => fileInputRef.current?.click()}
                                                >
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept=".json,application/json"
                                                        className="hidden"
                                                        onChange={handleFileChange}
                                                    />
                                                    
                                                    {selectedFile ? (
                                                        <>
                                                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                                                                <FileJson size={32} />
                                                            </div>
                                                            <h4 className="text-lg font-semibold text-slate-900 mb-1">
                                                                {selectedFile.name}
                                                            </h4>
                                                            <p className="text-sm text-slate-500">
                                                                {(selectedFile.size / 1024).toFixed(1)} KB
                                                            </p>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedFile(null);
                                                                    setValidationError(null);
                                                                    if (fileInputRef.current) {
                                                                        fileInputRef.current.value = '';
                                                                    }
                                                                }}
                                                                className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
                                                            >
                                                                Choose a different file
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="w-16 h-16 bg-white shadow-sm text-blue-500 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                                                <Upload size={28} />
                                                            </div>
                                                            <h4 className="text-lg font-semibold text-slate-900 mb-1">
                                                                Drop your file here
                                                            </h4>
                                                            <p className="text-sm text-slate-500 mb-2">
                                                                or click to browse
                                                            </p>
                                                            <p className="text-xs text-slate-400">
                                                                Accepts .json files
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            /* Paste Content */
                                            <div className="flex-1 flex flex-col">
                                                <Label className="mb-4 text-base font-medium text-slate-900">
                                                    Paste ASL Definition (JSON)
                                                </Label>
                                                
                                                <textarea
                                                    value={pastedContent}
                                                    onChange={(e) => {
                                                        setPastedContent(e.target.value);
                                                        setValidationError(null);
                                                    }}
                                                    placeholder={`{
  "Comment": "My workflow",
  "StartAt": "FirstState",
  "States": {
    "FirstState": {
      "Type": "Task",
      "Resource": "agent:my-agent",
      "End": true
    }
  }
}`}
                                                    className="flex-1 min-h-[300px] w-full rounded-lg border border-slate-200 bg-slate-900 p-4 font-mono text-sm text-slate-100 placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                                                    spellCheck={false}
                                                />
                                            </div>
                                        )}

                                        {/* Validation Error */}
                                        {validationError && (
                                            <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                                                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <h4 className="font-medium text-red-800">Validation Error</h4>
                                                    <p className="text-sm text-red-600 mt-1">{validationError}</p>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                )}

                                {/* Step 3: Confirmation */}
                                {step === 3 && parsedDefinition && (
                                    <motion.div
                                        key="step3"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="space-y-6"
                                    >
                                        {/* Success Banner */}
                                        <div className="flex items-start gap-3 rounded-lg border border-green-100 bg-green-50 p-4">
                                            <CheckCircle2 className="mt-0.5 text-green-600" size={20} />
                                            <div>
                                                <h3 className="font-medium text-green-800">
                                                    Definition Validated Successfully
                                                </h3>
                                                <p className="mt-1 text-sm text-green-700">
                                                    The ASL definition is valid and ready to import.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Definition Summary */}
                                        <div className="overflow-hidden rounded-lg border border-slate-200">
                                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                                                Definition Summary
                                            </div>
                                            <div className="p-4 space-y-4">
                                                {parsedDefinition.Comment && (
                                                    <div>
                                                        <span className="text-xs font-medium text-slate-500 uppercase">
                                                            Comment
                                                        </span>
                                                        <p className="mt-1 text-sm text-slate-900">
                                                            {parsedDefinition.Comment}
                                                        </p>
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <span className="text-xs font-medium text-slate-500 uppercase">
                                                            Start State
                                                        </span>
                                                        <p className="mt-1 text-sm font-mono text-slate-900">
                                                            {parsedDefinition.StartAt}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs font-medium text-slate-500 uppercase">
                                                            Total States
                                                        </span>
                                                        <p className="mt-1 text-sm text-slate-900">
                                                            {Object.keys(parsedDefinition.States || {}).length}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium text-slate-500 uppercase">
                                                        States
                                                    </span>
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {Object.entries(parsedDefinition.States || {}).map(([name, state]) => {
                                                            const stateType = (state as { Type?: string })?.Type || 'Unknown';
                                                            return (
                                                                <span
                                                                    key={name}
                                                                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                                                                >
                                                                    <span className="font-semibold">{name}</span>
                                                                    <span className="text-slate-400">·</span>
                                                                    <span className="text-slate-500">{stateType}</span>
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Warning */}
                                        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                            <AlertCircle className="mt-0.5 text-amber-600" size={20} />
                                            <div>
                                                <h4 className="font-medium text-amber-800">
                                                    This will replace the current definition
                                                </h4>
                                                <p className="mt-1 text-sm text-amber-700">
                                                    Importing will overwrite the existing workflow definition. This action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <div className="flex gap-3">
                                {step > 1 && (
                                    <button
                                        onClick={handleBack}
                                        disabled={loading}
                                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md disabled:opacity-50"
                                    >
                                        <ArrowLeft size={16} />
                                        Back
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleClose();
                                    }}
                                    disabled={loading}
                                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>

                            <div className="flex gap-3">
                                {step < 3 ? (
                                    <button
                                        onClick={handleNext}
                                        disabled={!canProceed || loading}
                                        className="flex items-center gap-2 rounded-lg bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-btn-primary-hover hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Next
                                        <ArrowRight size={16} />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleImport}
                                        disabled={loading}
                                        className="flex items-center gap-2 rounded-lg bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-btn-primary-hover hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Importing...
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={16} />
                                                Import Definition
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
        </SlideDrawer>
    );
}
